import React, { useEffect } from 'react';
import { Container, Row, Col, Card } from 'react-bootstrap';
import { 
  FaCheckCircle, 
  FaAward, 
  FaUsers, 
  FaHandshake,
  FaLightbulb,
  FaShieldAlt
} from 'react-icons/fa';
import CountUp from 'react-countup';
import './About.css';

const About = () => {
  useEffect(() => {
    document.title = 'About Us | Infrastructure Company';
  }, []);

  const stats = [
    { number: 15, suffix: '+', label: 'Years Experience' },
    { number: 500, suffix: '+', label: 'Projects Completed' },
    { number: 300, suffix: '+', label: 'Happy Clients' },
    { number: 50, suffix: '+', label: 'Expert Team' }
  ];

  const values = [
    {
      icon: <FaCheckCircle />,
      title: 'Quality First',
      description: 'We never compromise on quality and ensure the highest standards in every project.'
    },
    {
      icon: <FaHandshake />,
      title: 'Client Trust',
      description: 'Building long-term relationships with our clients through transparency and reliability.'
    },
    {
      icon: <FaLightbulb />,
      title: 'Innovation',
      description: 'Embracing latest technologies and innovative solutions for modern infrastructure.'
    },
    {
      icon: <FaShieldAlt />,
      title: 'Safety',
      description: 'Prioritizing safety of our workers and ensuring secure infrastructure for communities.'
    },
    {
      icon: <FaUsers />,
      title: 'Teamwork',
      description: 'Collaborative approach with skilled professionals working towards common goals.'
    },
    {
      icon: <FaAward />,
      title: 'Excellence',
      description: 'Striving for excellence in every aspect of our work and service delivery.'
    }
  ];

  const milestones = [
    { year: '2009', event: 'Company Founded', description: 'Started with a vision to transform infrastructure in India' },
    { year: '2012', event: 'First Major Project', description: 'Completed 50km highway cable installation in Maharashtra' },
    { year: '2015', event: 'ISO Certification', description: 'Received ISO 9001:2015 certification for quality management' },
    { year: '2018', event: '100+ Projects', description: 'Successfully completed over 100 infrastructure projects' },
    { year: '2020', event: 'Pan-India Expansion', description: 'Expanded operations across 15+ states in India' },
    { year: '2024', event: 'Industry Leader', description: 'Recognized as one of the top infrastructure companies in India' }
  ];

  return (
    <div className="about-page">
      {/* Page Header */}
      <section className="page-header">
        <Container>
          <div className="text-center">
            <h1 className="display-4 fw-bold mb-3">About Us</h1>
            <p className="lead">Building India's Future, One Project at a Time</p>
          </div>
        </Container>
      </section>

      {/* Company Overview */}
      <section className="section">
        <Container>
          <Row className="align-items-center">
            <Col lg={6} className="mb-4 mb-lg-0">
              <div className="about-image-wrapper">
                <img
                  src="https://images.unsplash.com/photo-1541888946425-d81bb19240f5?w=800"
                  alt="Construction Site"
                  className="img-fluid rounded shadow-lg"
                />
              </div>
            </Col>
            <Col lg={6}>
              <div className="ps-lg-4">
                <p className="section-subtitle">Our Story</p>
                <h2 className="section-title text-start">Transforming Infrastructure Across India</h2>
                <p className="text-muted mb-4" style={{ lineHeight: '1.8' }}>
                  Since 2009, we have been at the forefront of India's infrastructure development, 
                  delivering exceptional projects in underground cable wiring, road construction, 
                  electricity meter installations, and comprehensive infrastructure solutions.
                </p>
                <p className="text-muted mb-4" style={{ lineHeight: '1.8' }}>
                  Our commitment to quality, innovation, and client satisfaction has made us a 
                  trusted partner for government bodies, private enterprises, and communities 
                  across the nation. With a team of highly skilled professionals and state-of-the-art 
                  equipment, we continue to set new benchmarks in the industry.
                </p>
                <p className="text-muted mb-4" style={{ lineHeight: '1.8' }}>
                  We believe in sustainable development and implement eco-friendly practices in 
                  all our projects, ensuring we contribute positively to India's growth while 
                  preserving our environment for future generations.
                </p>
              </div>
            </Col>
          </Row>
        </Container>
      </section>

      {/* Stats Section */}
      <section className="stats-section-alt bg-gradient">
        <Container>
          <Row className="g-4">
            {stats.map((stat, index) => (
              <Col key={index} xs={6} lg={3}>
                <div className="stat-card-alt text-center">
                  <div className="stat-number-alt">
                    <CountUp end={stat.number} duration={2.5} suffix={stat.suffix} />
                  </div>
                  <div className="stat-label-alt">{stat.label}</div>
                </div>
              </Col>
            ))}
          </Row>
        </Container>
      </section>

      {/* Mission & Vision */}
      <section className="section">
        <Container>
          <Row className="g-4">
            <Col lg={6}>
              <Card className="mission-card h-100">
                <Card.Body className="p-4">
                  <div className="mission-icon mb-4">
                    <FaLightbulb />
                  </div>
                  <h3 className="mb-3">Our Mission</h3>
                  <p className="text-muted" style={{ lineHeight: '1.8' }}>
                    To deliver world-class infrastructure solutions that enhance connectivity, 
                    improve quality of life, and contribute to India's economic growth. We are 
                    committed to excellence, innovation, and sustainable development in every 
                    project we undertake.
                  </p>
                </Card.Body>
              </Card>
            </Col>
            <Col lg={6}>
              <Card className="vision-card h-100">
                <Card.Body className="p-4">
                  <div className="vision-icon mb-4">
                    <FaAward />
                  </div>
                  <h3 className="mb-3">Our Vision</h3>
                  <p className="text-muted" style={{ lineHeight: '1.8' }}>
                    To be India's most trusted and innovative infrastructure company, recognized 
                    for our commitment to quality, safety, and client satisfaction. We aspire to 
                    set industry standards and contribute significantly to building a modern, 
                    connected India.
                  </p>
                </Card.Body>
              </Card>
            </Col>
          </Row>
        </Container>
      </section>

      {/* Core Values */}
      <section className="section bg-light">
        <Container>
          <div className="section-header">
            <p className="section-subtitle">Our Values</p>
            <h2 className="section-title">What We Stand For</h2>
            <p className="section-description">
              Our core values guide everything we do and define who we are as a company
            </p>
          </div>

          <Row className="g-4">
            {values.map((value, index) => (
              <Col key={index} md={6} lg={4}>
                <Card className="value-card h-100">
                  <Card.Body className="text-center p-4">
                    <div className="value-icon mb-3">{value.icon}</div>
                    <h4 className="mb-3">{value.title}</h4>
                    <p className="text-muted mb-0">{value.description}</p>
                  </Card.Body>
                </Card>
              </Col>
            ))}
          </Row>
        </Container>
      </section>

      {/* Timeline */}
<section className="section">
  <Container>
    <div className="section-header">
      <p className="section-subtitle">Our Journey</p>
      <h2 className="section-title">Milestones & Achievements</h2>
      <p className="section-description">
        Key moments that shaped our growth and success over the years
      </p>
    </div>

    <div className="timeline">
      {milestones.map((milestone, index) => (
        <div key={index} className={`timeline-item ${index % 2 === 0 ? 'left' : 'right'}`}>
          <div className="timeline-content">
            <div className="timeline-year">{milestone.year}</div>
            <h4>{milestone.event}</h4>
            <p className="text-muted mb-0">{milestone.description}</p>
          </div>
        </div>
      ))}
    </div>
  </Container>
</section>

      {/* Certifications */}
      <section className="section bg-gradient">
        <Container>
          <div className="text-center text-white">
            <h2 className="fw-bold mb-4">Certified & Recognized</h2>
            <p className="lead mb-4">
              ISO 9001:2015 Certified | NSIC Registered | MSME Certified
            </p>
            <div className="d-flex flex-wrap justify-content-center gap-4 mt-5">
              <div className="certification-badge">
                <FaAward className="mb-2" style={{ fontSize: '3rem' }} />
                <div>ISO 9001:2015</div>
              </div>
              <div className="certification-badge">
                <FaShieldAlt className="mb-2" style={{ fontSize: '3rem' }} />
                <div>Safety Certified</div>
              </div>
              <div className="certification-badge">
                <FaCheckCircle className="mb-2" style={{ fontSize: '3rem' }} />
                <div>Quality Assured</div>
              </div>
            </div>
          </div>
        </Container>
      </section>

      {/* Indian Accent */}
      <div className="indian-accent"></div>
    </div>
  );
};

export default About;